import React, { useMemo, useContext, Fragment } from 'react';
import { Skeleton } from '@material-ui/lab';
import {
  ADD_PRODUCT_INFO, IPRODUCT_COLLECTION, PRODUCT_TO_BE_ADDED,
  CONFIRMATION_POPUP_KEY, RESPONSE_POPUP_KEY, MODAL_KEY,
  PRODUCT_INFO,
} from '../../../constants/patientChart.constant';
import { PatientChartContext } from '../../../contexts/patientChart';
import PPButton from '../../subComponents/button';
import Input from '../../subComponents/input';
import Select from '../../subComponents/select';
import { POPUP_TYPE } from '../../subComponents/popup';
import { getLoggedInUser } from '../../../utils/jose';

export const AddProduct = ({
  orderId = 0,
  product = PRODUCT_INFO(),
  productCost = {},
  addProductInfo = ADD_PRODUCT_INFO(),
  productsInAddProductList: products = [IPRODUCT_COLLECTION()],
  productCanBeAddedCollection: productCollection = new Map([[-1, IPRODUCT_COLLECTION()]]),

  getProductCost = async () => {},
  resetProductCost = async () => {},
  addProduct = async () => {},
  getNotesTabInfo = async () => {},
}) => {
  const { handleModalOpen, handleMultipleModalClose, productToBeAdded, setProductToBeAdded, handleLoadProductsListing } = useContext(PatientChartContext);

  const doctorId = useMemo(() => getLoggedInUser().UserId, []);

  const { existingProductNamesConcatenated } = useMemo(() => {
    const existingProductNames = [];
    let existingProductNamesConcatenated = '';
    for (let i = 0; i < product.Product.length; i++) {
      const _product = product.Product[i];
      existingProductNames.push(_product.displayTitle);

      if (existingProductNamesConcatenated.length)
        existingProductNamesConcatenated += ', ';
      existingProductNamesConcatenated += _product.displayTitle;
    }
    return { existingProductNames, existingProductNamesConcatenated };
  }, [product]);

  const storefrontCompanyId = useMemo(() => {
    if (!product?.StoreFrontData?.CompanyId) {
      return 0;
    }
    return Number(product.StoreFrontData.CompanyId);
  }, [product]);

  const { _productCost, _shippingCost, _totalOrderCost } = useMemo(() => {
    try {
      const { ProductCost, ProductShippingCost, TotalOrderCost } = productCost.api.data;
      const _productCost = Math.round(ProductCost * productToBeAdded.summary.quantity * 100) / 100;
      const _shippingCost = Math.round(ProductShippingCost * 100) / 100;
      const _totalOrderCost = Math.round((TotalOrderCost + _productCost) * 100) / 100;
      return {
        _productCost,
        _shippingCost,
        _totalOrderCost,
      };
    } catch (e) {
      // 
    }

    return {
      _productCost: 0,
      _shippingCost: 0,
      _totalOrderCost: 0,
    };
  }, [productCost.api.data, productToBeAdded.summary.quantity]);

  const handleChange = async ({ target }) => {
    const productId = Number(target.value);
    const product = productCollection.get(productId);

    setProductToBeAdded({
      ...productToBeAdded,
      value: target.value,
      summary: {
        productId: product.id,
        product: product.label,
        pharmacy: product.summary.pharmacy,
        shipper: product.summary.shipper,
        quantity: product.summary.quantity,
      },
    });

    if (Number(target.value) === -1) {
      resetProductCost();
      return {};
    }

    try {
      const body = {
        orderId,
        productId,
        storefrontCompanyId,
      };
      await getProductCost(body);
    } catch (e) {
      // 
    }

    return {};
  };

  const SkeletonLoader = <Skeleton variant='text' className='flex width-100' />;

  const ProductCostComponent = new Map([
    ['false-false', <Fragment />],
    ['false-true', SkeletonLoader],
    ['true-false', `$${_productCost}`],
    ['true-true', SkeletonLoader],
  ]);

  const ProductShippingCostComponent = new Map([
    ['false-false', <Fragment />],
    ['false-true', SkeletonLoader],
    ['true-false', `$${_shippingCost}`],
    ['true-true', SkeletonLoader],
  ]);

  const TotalOrderCost = new Map([
    ['false-false', <Fragment />],
    ['false-true', SkeletonLoader],
    ['true-false', `$${_totalOrderCost}`],
    ['true-true', SkeletonLoader],
  ]);

  const handleAddProduct = async ({ keys }) => {
    handleMultipleModalClose({ keys: [CONFIRMATION_POPUP_KEY.ADD_NEW_PRODUCT] });

    try {
      const body = {
        orderId: product.OrderId,
        productId: productToBeAdded.summary.productId,
        doctorId: doctorId,
        productCost: productCost.api.data.ProductCost,
        totalOrderCost: _totalOrderCost,
        qty: productToBeAdded.summary.quantity,
        storefrontCompanyId,
      };

      const { status, data } = await addProduct(body);
      if (status === 200) {
        handleMultipleModalClose.call(this, { keys });
        setProductToBeAdded(PRODUCT_TO_BE_ADDED());
        resetProductCost();
        handleLoadProductsListing({ OrderId: product.OrderId });

        // reload the notes
        getNotesTabInfo();
      }

      if (status !== 200) {
        const { Error: error = 'Failed to add product. Please try later.' } = data;
        const heading = 'Error';

        handleModalOpen({
          key:
          RESPONSE_POPUP_KEY.ADD_NEW_PRODUCT, modal: {
            heading,
            type: POPUP_TYPE.ERROR,
            message: error,
          }
        });
      }
    } catch (e) {
      // 
    }
  };

  const Template = (
    <div className='flex flex-column flex-fill m-b-5'>
      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="input-label font-roboto m-b-2">Add New Product:</label>
        <Select
          id='productId'
          labelId="productId"
          value={productToBeAdded.value}
          onChange={handleChange}
          items={products}
        />
      </div>

      <div className='flex flex-column justify-content-center m-b-5 d-none'>
        <label className="input-label font-roboto m-b-2">Pharmacy:</label>
        <Input
          name='pharmacy'
          type='text'
          label=''
          className='flex flex-fill m-b-2'
          labelClassName=''
          value={productToBeAdded.summary.pharmacy}
          error={false}
          message={''}
          onChange={() => {}}
        />
        <div className='flex font-roboto font-14'>{productToBeAdded.summary.pharmacy}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5 d-none'>
        <label className="input-label font-roboto m-b-2">Select Shipping Services:</label>
        <Input
          name='pharmacy'
          type='text'
          label=''
          className='flex flex-fill m-b-2'
          labelClassName=''
          value={productToBeAdded.summary.shipper}
          error={false}
          message={''}
          onChange={() => {}}
        />
        <div className='flex font-roboto font-14'>{productToBeAdded.summary.shipper}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-2'>
        <label className="input-label font-roboto m-b-2">Summary</label>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Product:</label>
        <div className='flex font-roboto font-14 bold'>{productToBeAdded.summary.product}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Quantity:</label>
        <div className='flex font-roboto font-14 bold'>{productToBeAdded.summary.quantity}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Pharmacy:</label>
        <div className='flex font-roboto font-14 bold'>{productToBeAdded.summary.pharmacy}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Shipper:</label>
        <div className='flex font-roboto font-14 bold'>{productToBeAdded.summary.shipper}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Product Cost:</label>
        <div className='flex font-roboto font-14 bold'>{ProductCostComponent.get(`${productCost.api.called}-${productCost.api.loading}`)}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Shipping Cost:</label>
        <div className='flex font-roboto font-14 bold'>{ProductShippingCostComponent.get(`${productCost.api.called}-${productCost.api.loading}`)}</div>
      </div>

      <div className='flex flex-column justify-content-center m-b-5'>
        <label className="font-roboto font-14 m-b-2">Total Cost:</label>
        <div className='flex font-roboto font-14 bold'>{TotalOrderCost.get(`${productCost.api.called}-${productCost.api.loading}`)}</div>
      </div>

      <div className='flex flex-column font-roboto font-14 bold'>
        Please inform the patient about total order cost of the prescription that will be charged upon approval.
      </div>
    </div>
  );

  const Actions = (
    <div className='flex justify-content-flex-end'>
      <PPButton
        label='ADD NEW PRODUCT'
        variant='contained'
        loading={addProductInfo.api.loading}
        disabled={productToBeAdded.value === -1}
        endGutter
        onClick={handleModalOpen.bind(this, {
          key: CONFIRMATION_POPUP_KEY.ADD_NEW_PRODUCT,
          modal: {
            has: true,
            heading: 'Confirmation',
            message: `Confirm you are adding product ${productToBeAdded.summary.product} to this order along with the current product(s) ${existingProductNamesConcatenated}.`,
            type: POPUP_TYPE.INFO,
            onConfirm: handleAddProduct.bind(this, { keys: [MODAL_KEY.ADD_NEW_PRODUCT, CONFIRMATION_POPUP_KEY.ADD_NEW_PRODUCT] }),
            onCancel: handleMultipleModalClose.bind(this, { keys: [CONFIRMATION_POPUP_KEY.ADD_NEW_PRODUCT] }),
          },
        })}
      />
      <PPButton label='CANCEL' variant='cancel' onClick={handleMultipleModalClose.bind(this, { keys: [MODAL_KEY.ADD_NEW_PRODUCT] })} />
    </div>
  );

  return (
    <div className='flex flex-column flex-fill m-5 patient-chart-container'>
      {Template}
      {Actions}
    </div>
  );
};

export default AddProduct;
